<script setup lang="tsx">
import * as Chartist from 'chartist'
import { ref, onBeforeMount, onMounted, watch, nextTick, computed } from 'vue'
import http from '@/services/http'
import type { DueDateMethodsData, Response } from '@/models/api-generated'
import { useI18n } from 'vue-i18n'
import {
	DueDateOnTime,
	DueDateApproaching,
	DueDateCritical,
	DueDateOverdue,
	NeedsAttention,
	NotImplemented,
	PartiallyImplemented,
	WillNotApply,
	WillNotImplement,
	PermOwnMeasureRead,
	PermAllMeasureRead,
} from '@/models/enums-generated'
import { generateDueDateParams, hasPermission } from '@/services/utils'

interface Model {
	[key: string]: DueDateMethodsData
}

const currentUser = sessionMap.user
const chart = ref(null)
const result = ref<Model>()
const style = ref<string>('height:0px;')
const { t } = useI18n()
const canViewMethods = computed(() => hasPermission(PermOwnMeasureRead, PermAllMeasureRead))

const statusMap: Record<string, string> = {
	pie1: DueDateOnTime,
	pie2: DueDateApproaching,
	pie3: DueDateCritical,
	pie4: DueDateOverdue,
}

function goToMethodsPage(id: number, className: string) {
	const params = new URLSearchParams()
	params.append('instance', `${id}`)

	const dueDateParams = generateDueDateParams(statusMap[className])
	for (const [key, value] of dueDateParams.entries()) {
		params.append(key, value)
	}

	params.append(`status`, NotImplemented)
	params.append(`status`, WillNotApply)
	params.append(`status`, WillNotImplement)
	params.append(`status`, PartiallyImplemented)
	params.append(`status`, NeedsAttention)
	window.location.href = `/app/methods?${params}`
}
function getLabel(className: string) {
	if (className === 'pie1') return t('date.onTime')
	else return t('date.' + statusMap[className])
}

const opts: Chartist.BarChartOptions = {
	horizontalBars: true,
	reverseData: true,
	seriesBarDistance: 25,
	chartPadding: {
		right: 20,
	},
	axisX: {
		onlyInteger: true,
		labelOffset: {
			x: 0,
			y: 12,
		},
	},
	axisY: {
		offset: 100,
	},
}

let barChart: Chartist.BarChart

onBeforeMount(async () => {
	if (!canViewMethods.value) {
		result.value = {}
		return
	}
	const { data } = await http.get<Response<Model>>('api/dashboard/dueDateMethods')
	result.value = data.data
})

const instanceIds: number[] = []

onMounted(() => {
	barChart = new Chartist.BarChart(chart.value, { series: [] }, opts)
	barChart.on('draw', (e) => {
		if (e.type === 'bar') {
			const value = Chartist.isMultiValue(e.value) ? (e.value as any).x : (e.value as number)
			const instanceId = instanceIds[e.index] || null

			const className = (e.series as any)?.className || 'unknown'
			const barElement = e.element.getNode()
			barElement.setAttribute('title', `${getLabel(className)}: ${value}`)
			barElement.setAttribute('data-bs-toggle', 'tooltip')
			if (instanceId) {
				barElement.addEventListener('click', () => goToMethodsPage(instanceId, className))
			}

			new Chartist.Svg(
				'text',
				{
					x: e.x2 + 4,
					y: e.y1,
					'dominant-baseline': 'central',
					class: 'text-colored',
				},
				undefined,
				e.group,
			).text(value.toString())
		}
	})
})

watch(result, (data) => {
	if (!data) return

	const height = Object.keys(data).length * 128
	style.value = `height:${height}px;`

	nextTick(() => {
		const labels: string[] = []
		const ontime: number[] = []
		const approaching: number[] = []
		const critical: number[] = []
		const overdue: number[] = []

		for (const [key, value] of Object.entries(data)) {
			const [idStr, label] = key.split('*')
			const instanceId = Number(idStr)

			labels.push(label)

			ontime.push(value.ontime)
			approaching.push(value.approaching)
			critical.push(value.critical)
			overdue.push(value.overdue)
			instanceIds.push(instanceId)
		}

		instanceIds.reverse()
		barChart.update({
			labels,
			series: [
				{ className: 'pie1', data: ontime },
				{ className: 'pie2', data: approaching },
				{ className: 'pie3', data: critical },
				{ className: 'pie4', data: overdue },
			],
		})
	})
})
</script>

<template>
	<p class="text-center fw-bold">
		{{ $t('dashboard.duedateMethods') }}
		<template v-if="hasPermission(PermOwnMeasureRead) && !hasPermission(PermAllMeasureRead)">
			- {{ $t('user.label') }} {{ currentUser.firstname }} {{ currentUser.lastname }}</template
		>
		<template v-else-if="!canViewMethods"> - {{ $t('roles.missingRights') }}</template>
	</p>

	<div ref="chart" class="duedate-chart" :style="style"></div>

	<div v-if="!result" class="p-4 text-center">
		<div class="spinner-border text-primary"></div>
	</div>
</template>

<style lang="sass">
.duedate-chart
    .ct-label
        color: var(--bs-body-color) !important

.duedate-chart .ct-bar
    cursor: pointer
    stroke-width: 25px
    transition: opacity 0.2s ease

    &:hover
        opacity: 0.7
</style>
